import React, { useEffect } from "react";
import { Button, Card, Tag, Typography, Space, List, Popconfirm, message } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchChangeOrderReviews, deleteChangeOrderReview } from "../../services/contractsAPI";
import { fetchProjectUsers } from "../../services/projectsAPI";
import AttachmentsView from "../AttachmentsView";
import { formatDate } from "../../utils/dateUtils";

const { Text, Title } = Typography;

const ChangeOrderReviewSection = ({
  changeOrder,
  isViewMode,
  currentUserId,
  assigneeId,
  onReviewClick,
  userRole,
  coLabel,
}) => {
  const canCreateReview = currentUserId === assigneeId;
  const canDeleteReview =
    userRole === "project_manager" || userRole === "owner";
  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: ({ contractId, changeOrderId, projectId, reviewId }) =>
      deleteChangeOrderReview({
        contractId,
        changeOrderId,
        projectId,
        reviewId,
      }),
    onSuccess: () => {
      message.success("Review deleted successfully");
      queryClient.invalidateQueries(["changeOrderReviews", changeOrder._id]);
    },
    onError: (error) => {
      message.error(
        error.response?.data?.detail ||
          error.message ||
          "Failed to delete review",
      );
    },
  });

  const handleDeleteReview = (reviewId) => {
    deleteMutation.mutate({
      contractId: changeOrder.contract_id,
      changeOrderId: changeOrder._id,
      projectId: changeOrder.project_id,
      reviewId,
    });
  };

  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ["changeOrderReviews", changeOrder._id],
    queryFn: () =>
      fetchChangeOrderReviews({
        contractId: changeOrder.contract_id,
        changeOrderId: changeOrder._id,
        projectId: changeOrder.project_id,
      }),
    enabled: !!changeOrder._id,
  });

  const { data: projectUsers, isLoading: usersLoading } = useQuery({
    queryKey: ["projectUsers", changeOrder.project_id],
    queryFn: () => fetchProjectUsers(changeOrder.project_id),
    enabled: !!changeOrder.project_id,
  });

  const reviews = reviewsData?.reviews || [];

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "revise_and_resubmit":
        return "warning";
      case "pending":
        return "processing";
      default:
        return "default";
    }
  };

  if (reviewsLoading || usersLoading) {
    return (
      <Card className="mt-4">
        <Text>Loading reviews...</Text>
      </Card>
    );
  }

  if (!reviews.length) {
    if (canCreateReview) {
      return (
        <Card className="mt-4">
          <div className="flex items-center justify-between">
            <Text type="secondary">
              You are assigned to review this {coLabel.toLowerCase()}.
            </Text>
            <Button type="primary" onClick={onReviewClick}>
              Add Review
            </Button>
          </div>
        </Card>
      );
    }
    return (
      <Card className="mt-4">
        <Text type="secondary">No reviews yet.</Text>
      </Card>
    );
  }

  return (
    <Card className="mt-4">
      <List
        itemLayout="vertical"
        dataSource={reviews}
        renderItem={(review) => (
          <List.Item
            extra={
              <Space>
                {currentUserId === review.reviewer_id && (
                  <Button
                    type="primary"
                    icon={<EditOutlined />}
                    onClick={() => onReviewClick(review)}
                  />
                )}
                {canDeleteReview && (
                  <Popconfirm
                    title="Delete Review"
                    description="Are you sure you want to delete this review? This action cannot be undone."
                    onConfirm={() => handleDeleteReview(review._id)}
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    okType="danger"
                  >
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      loading={deleteMutation.isPending}
                    />
                  </Popconfirm>
                )}
              </Space>
            }
          >
            <div className="flex-grow">
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Space direction="vertical" size="small">
                  <Space>
                    <Text strong>Status:</Text>
                    <Tag color={getStatusColor(review.status)}>
                      {review.status.charAt(0).toUpperCase() +
                        review.status.slice(1)}
                    </Tag>
                  </Space>
                  <Space direction="vertical" size="small">
                    <Text type="secondary">
                      Reviewed By:{" "}
                      {projectUsers?.data?.find(
                        (u) => u.id === review.reviewer_id,
                      )?.username || "Unknown"}
                    </Text>
                    <Text type="secondary">
                      Reviewed On: {formatDate(review.created_at)}
                    </Text>
                  </Space>
                </Space>

                {review.notes && (
                  <div>
                    <Text strong>Notes:</Text>
                    <Text className="block mt-1">{review.notes}</Text>
                  </div>
                )}

                {review.attachments?.length > 0 && (
                  <div>
                    <Text strong className="mb-2 block">
                      Review Attachments:
                    </Text>
                    <AttachmentsView attachments={review.attachments} />
                  </div>
                )}
              </Space>
            </div>
          </List.Item>
        )}
      />
      {canCreateReview &&
        !reviews.some((review) => review.reviewer_id === currentUserId) && (
          <div className="mt-4">
            <Button type="primary" onClick={() => onReviewClick()}>
              Add Review
            </Button>
          </div>
        )}
    </Card>
  );
};

export default ChangeOrderReviewSection;
